/**
 * SAMBA configuration API types
 */

import {
    MerchantConfigSchema,
    type MerchantConfig,
    type UserRoleAssignmentConfigPatch,
} from './backend_config.types';
/**
 * Config response (single config object)
 */
export type ConfigResponse = MerchantConfig;

/**
 * Update config request
 */
export interface UpdateConfigRequest {
    config: MerchantConfig;
}

export interface PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest {
    userRoleAssignmentConfig: UserRoleAssignmentConfigPatch;
}

/**
 * Update config response
 */
export type UpdateConfigResponse = MerchantConfig;

/**
 * Combined config response
 */
export interface CombinedConfigResponse {
    user_config: ConfigResponse;
    org_config: ConfigResponse;
}

/**
 * Role assignment config response
 * Used when getting/updating config for a specific role assignment
 */
export interface RoleAssignmentConfigResponse {
    role_assignment_id: number;
    user_config: MerchantConfig;
}

/**
 * Get effective config response
 */
export interface GetEffectiveConfigResponse {
    config: MerchantConfig;
    version: string;
}

/**
 * Merge org_config and user_config
 * User config takes precedence over org config
 */
export function mergeConfigs(
    orgConfig?: ConfigResponse,
    userConfig?: ConfigResponse
): ConfigResponse {
    const organizationConfig: MerchantConfig = MerchantConfigSchema.parse(orgConfig ?? {});
    const roleAssignmentConfig: MerchantConfig = MerchantConfigSchema.parse(userConfig ?? {});
    const organizationPlatformConfig = orgConfig?.platformConfig;
    const roleAssignmentPlatformConfig = userConfig?.platformConfig;
    const organizationCustomerPortal =
        organizationPlatformConfig?.features.customerPortal ?? null;
    const roleAssignmentCustomerPortal =
        roleAssignmentPlatformConfig?.features.customerPortal ?? null;
    const mergedCustomerPortal =
        organizationCustomerPortal !== null || roleAssignmentCustomerPortal !== null
            ? {
                  ...organizationCustomerPortal,
                  ...roleAssignmentCustomerPortal,
                  paymentRequests:
                      organizationCustomerPortal?.paymentRequests ||
                      roleAssignmentCustomerPortal?.paymentRequests
                          ? {
                                ...organizationCustomerPortal?.paymentRequests,
                                ...roleAssignmentCustomerPortal?.paymentRequests,
                            }
                          : undefined,
              }
            : null;

    return MerchantConfigSchema.parse({
        ipg: {
            ...organizationConfig.ipg,
            ...roleAssignmentConfig.ipg,
        },
        ipw: {
            ...organizationConfig.ipw,
            ...roleAssignmentConfig.ipw,
        },
        processorConfig:
            roleAssignmentConfig.processorConfig.length > 0
                ? roleAssignmentConfig.processorConfig
                : organizationConfig.processorConfig,
        sambaConfig: roleAssignmentConfig.sambaConfig ?? organizationConfig.sambaConfig,
        platformConfig:
            organizationPlatformConfig !== undefined || roleAssignmentPlatformConfig !== undefined
                ? {
                      features: {
                          payment_methods: {
                              ...organizationPlatformConfig?.features.payment_methods,
                              ...roleAssignmentPlatformConfig?.features.payment_methods,
                          },
                          customerPortal: mergedCustomerPortal,
                      },
                      payment_request_email_cc:
                          roleAssignmentPlatformConfig?.payment_request_email_cc ??
                          organizationPlatformConfig?.payment_request_email_cc ??
                          [],
                  }
                : undefined,
    });
}
